import React from 'react';
import './HomePage.css';

// Import images
import heroImage from '../assets/images/teacher-4784917_1920.jpg';
import studentsImage from '../assets/images/students-99506_1920.jpg';
import digitalWellbeing from '../assets/images/pavement-3365703_1920.jpg';

const HomePage = () => {
  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="logo-container">
                <img 
                  src="https://static.wixstatic.com/media/1d90b3_588e219d6cc2449d84256529ea18235d~mv2.png" 
                  alt="Digital Flourishing for Young Minds" 
                  className="hero-logo"
                />
              </div>
              <h1>Digital Flourishing for Young Minds</h1>
              <h2>Transform Your School's Digital Culture</h2>
              <p className="hero-subheadline">Building resilience, focus, and healthy digital habits in young people.</p>
              <p className="hero-description">
                The complete, research-based program empowering educators and parents to 
                foster healthy digital habits in today's students.
              </p>
              <button className="btn btn-primary hero-btn">
                Contact Us to Learn More
              </button>
            </div>
            <div className="hero-image">
              <img src={heroImage} alt="Teacher engaging with students" />
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Statistics Section */}
      <section className="section section-light">
        <div className="container">
          <div className="crisis-content">
            <div className="crisis-text">
              <h3>The Digital Crisis Facing Today's Schools</h3>
              <div className="crisis-stats">
                <p>UK students spend <strong>6.5+ hours daily</strong> on screens outside of school</p>
                <p><strong>82%</strong> of UK schools report digital distraction as a major classroom challenge</p>
                <p>Teachers lose <strong>15%</strong> of instructional time to technology disruptions</p>
                <p><strong>68%</strong> of UK teenagers report sleep issues linked to screen time</p>
                <p><strong>35%</strong> increase in anxiety among UK young people correlates with social media use</p>
                <p>UK TESS data shows <strong>73%</strong> of teachers report student attention difficulties related to digital device use</p>
              </div>
            </div>
            <div className="crisis-image">
              <img src={digitalWellbeing} alt="Digital wellness concept" />
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <section className="section section-white">
        <div className="container">
          <div className="framework-header text-center">
            <h3>The First Complete Framework for Digital Wellbeing</h3>
            <p>
              Digital Flourishing for Young Minds transforms how schools approach technology through our 
              four-pillar framework. This framework has been implemented in over 25 diverse educational 
              settings, yielding measurable improvements in attention, behaviour, well-being, and academic outcomes.
            </p>
          </div>

          <div className="pillars-grid">
            <div className="pillar-card">
              <div className="pillar-number">1</div>
              <h4>Digital Awareness</h4>
              <p>Understanding the digital landscape and its neurological effects on developing minds.</p>
            </div>
            <div className="pillar-card">
              <div className="pillar-number">2</div>
              <h4>Digital Detox</h4>
              <p>Evidence-based strategies for healthy boundaries and balance in school and home environments.</p>
            </div>
            <div className="pillar-card">
              <div className="pillar-number">3</div>
              <h4>Digital Intentionality</h4>
              <p>Purpose-driven technology use aligned with values, goals, and developmental needs.</p>
            </div>
            <div className="pillar-card">
              <div className="pillar-number">4</div>
              <h4>Digital Flourishing</h4>
              <p>Leveraging technology for growth, creativity, and wellbeing while minimizing harmful impacts.</p>
            </div>
          </div>

          <div className="framework-image-section">
            <img src={studentsImage} alt="Students learning with technology" />
          </div>
        </div>
      </section>

      {/* Three-Pillar Approach Section */}
      <section className="section section-white">
        <div className="container">
          <div className="three-pillar-header text-center">
            <h3>Our Complete 3-Pillar Approach</h3>
            <p>
              Digital Flourishing for Young Minds provides comprehensive support for the entire school community.
              We work with teachers, parents, and students to create a unified culture of digital wellness.
            </p>
          </div>

          <div className="three-pillar-grid">
            <div className="pillar-approach-card">
              <div className="pillar-icon">👨‍🏫</div>
              <h4>For Teachers</h4>
              <p>Professional development programs that equip educators with strategies to manage digital distraction, support student wellbeing, and create intentional technology use in the classroom.</p>
              <ul>
                <li>6-session professional development program</li>
                <li>Classroom management strategies</li>
                <li>Student support techniques</li>
                <li>Evidence-based practices</li>
              </ul>
            </div>

            <div className="pillar-approach-card">
              <div className="pillar-icon">👨‍👩‍👧</div>
              <h4>For Parents</h4>
              <p>Practical workshops and resources to help parents understand digital technology, set healthy boundaries, and support their children's digital wellness at home.</p>
              <ul>
                <li>4-session parent workshop series</li>
                <li>Conversation starters and templates</li>
                <li>Screen time management strategies</li>
                <li>Family digital wellness planning</li>
              </ul>
            </div>

            <div className="pillar-approach-card">
              <div className="pillar-icon">👦</div>
              <h4>For Students</h4>
              <p>Age-appropriate talks and activities that help students understand technology's impact, develop digital awareness, and build intentional technology use habits.</p>
              <ul>
                <li>Age-tailored talks (Year 5-12+)</li>
                <li>Interactive activities and discussions</li>
                <li>Practical strategies for digital wellness</li>
                <li>Peer support and accountability</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="section section-yellow">
        <div className="container text-center">
          <h3>Transform Your School's Digital Culture</h3>
          <p>
            Schedule a complimentary 30-minute consultation with our implementation specialists 
            to discuss your school's specific needs.
          </p>
          <button className="btn btn-secondary">
            Contact Us to Learn More
          </button>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section section-teal">
        <div className="container text-center">
          <h3 style={{color: 'white', marginBottom: '1rem'}}>Our Mission</h3>
          <p style={{color: 'white', fontSize: '1.3rem', margin: 0}}>
            Technology that Serves Human Flourishing
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
